/**
 * Meet room state machine. Transport-agnostic so the Node server runs it over WebSockets
 * and the browser runs it in-process for solo practice. The server is authoritative: every
 * athlete's sim lives here, fed by human input batches or bot brains, and snapshots go out
 * at 10 Hz. Clients predict their own athlete with the same sim.
 *
 * Track events run simultaneously in lanes. Field events run turn by turn like a real meet:
 * one athlete on the runway with a clock, the rest watching; long jump in rounds of
 * attempts, high jump from the lowest bar upwards.
 */
import { BOT_NAMES, botBeginTurn, createBot, stepBot, type BotBrain } from './bots.js';
import { standardFor, type EventDef } from './events.js';
import {
  applyMark,
  createMeetAthlete,
  eventPlaces,
  leaguePoints,
  programmeEvents,
  standings,
  type MeetAthlete,
} from './meet.js';
import { parseClientMessage } from './validation.js';
import { sanitiseName, type ResultRow } from './messages.js';
import {
  MAX_PLAYERS,
  type ClientMessage,
  type EventResultRow,
  type InputBatch,
  type MeetPhase,
  type MeetResultRow,
  type PlayerInfo,
  type RoomOptions,
  type ServerMessage,
} from './protocol.js';
import { regularity } from './input.js';
import { InputReplay, INPUT_WINDOW, type TimedInput } from './input-replay.js';
import { mulberry32 } from './rng.js';
import {
  ATTEMPT_CLOCK,
  createSim,
  MARKS_LEAD,
  simAdvance,
  simBeginTurn,
  simFinishTurn,
  simMark,
  simSnapshot,
  simTurnEndsAt,
  simTurnOver,
  runCapFor,
  type AthleteSim,
} from './sim/athlete.js';

export interface RoomHost {
  now(): number;
  send(id: string, msg: ServerMessage): void;
  broadcast(msg: ServerMessage): void;
  /** Persist a finished meet (humans' results and league points). */
  recordMeet(results: MeetResultRow[], programme: RoomOptions['programme'], meetId?: string): void;
  recordEvent?(key: string, name: string, row: ResultRow, resultKey: string): void;
  newMeetId?(): string;
  getLeague(): Extract<ServerMessage, { type: 'league' }>;
  log?(msg: string): void;
}

export const DEFAULT_ROOM_OPTIONS: RoomOptions = {
  programme: 'full',
  fillWithBots: true,
  botLevel: 'county',
};
export const TICK_MS = 50;
export const SNAPSHOT_EVERY = 2;
export const INTRO_MS = 8000;
export const RESULTS_MS = 9000;
export const PODIUM_MS = 40_000;
/** Track events: "Set" comes this long (random within the range) before the gun. */
const SET_MIN = 1.3;
const SET_MAX = 2.2;
/** Events cannot run longer than this (s of event time). */
export const RECONNECT_MS = 30_000;
const FIELD_CAP = 15 * 60;
const KIT_RE = /^#[0-9a-f]{6}$/i;

export function isTurnBased(ev: EventDef): boolean {
  return ev.kind === 'jump' || ev.kind === 'vault' || ev.kind === 'throw';
}

/** Turn order key: attempts taken (long jump) or bar height in cm (high jump). */
function turnKey(s: AthleteSim): number {
  if (s.kind === 'lj' || s.kind === 'sp') return s.state.attempt;
  if (s.kind === 'hj') return s.state.bar * 100;
  return 0;
}

/** Kit palette for bots and for humans whose colour clashes. */
const KIT_PALETTE = [
  '#e53935',
  '#1e88e5',
  '#fdd835',
  '#7cb342',
  '#fb8c00',
  '#8e24aa',
  '#00acc1',
  '#ec407a',
  '#f5f5f5',
  '#263238',
];

interface Player {
  replay: InputReplay;
  info: PlayerInfo;
  meet: MeetAthlete;
  sim: AthleteSim | null;
  bot: BotBrain | null;
  /** Last accepted input time, to keep presses monotonic per athlete. */
  lastInputT: number;
  ack: number;
  disconnectedUntil: number | null;
  playerKey?: string;
  metrics: {
    sum: number;
    samples: number;
    peak: number;
    presses: number;
    rejected: number;
    lastCount: number;
    lastRejected: number;
    intervals: number[];
  };
  /** Sliding window for rate limiting (event-time seconds of recent presses). */
  recent: number[];
}

export class Room {
  phase: MeetPhase = 'lobby';
  options: RoomOptions = { ...DEFAULT_ROOM_OPTIONS };
  season = 1;
  private players = new Map<string, Player>();
  private events: EventDef[] = [];
  private eventIndex = -1;
  /** Server time that event sim time zero refers to. */
  private refAt = 0;
  private tickHandle: ReturnType<typeof setInterval> | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private tickNo = 0;
  private rng = mulberry32(1);
  private lanes: Record<string, number> = {};
  /** Turn-based field events: whose go it is. */
  private activeId: string | null = null;
  private meetId = '';
  private meetNo = 0;
  private startMessage: Extract<ServerMessage, { type: 'eventStart' }> | null = null;
  private turnMessage: Extract<ServerMessage, { type: 'turn' }> | null = null;
  private resultMessage: Extract<ServerMessage, { type: 'eventResults' | 'podium' }> | null = null;

  constructor(private host: RoomHost) {}

  // ---------------------------------------------------------------- queries
  get humanCount(): number {
    return [...this.players.values()].filter((p) => !p.info.isBot).length;
  }
  get playerList(): PlayerInfo[] {
    return [...this.players.values()].map((p) => p.info);
  }
  get currentEvent(): EventDef | null {
    return this.events[this.eventIndex] ?? null;
  }

  // --------------------------------------------------------------- messages
  handle(id: string, msg: ClientMessage): void {
    const valid = parseClientMessage(msg);
    if (!valid) {
      this.host.send(id, { type: 'error', message: 'Bad message' });
      return;
    }
    msg = valid;
    switch (msg.type) {
      case 'leave':
        return this.leave(id);
      case 'nextSeason': {
        if (
          this.phase !== 'podium' ||
          !this.players.get(id)?.info.isHost ||
          this.options.programme !== 'full' ||
          this.eventIndex !== this.events.length
        )
          return;
        this.season++;
        this.clearTimer();
        this.beginMeet();
        return;
      }
      case 'setHeight': {
        const p = this.players.get(id);
        const s = p?.sim;
        if (
          this.activeId !== id ||
          s?.kind !== 'hj' ||
          s.state.phase !== 'runup' ||
          s.state.runner.cadence.count > 0 ||
          s.state.runner.pending.length
        )
          return;
        if (msg.height >= s.state.bar && msg.height <= (s.cfg.vault ? 6.5 : 2.5)) {
          s.state.bar = Math.round(msg.height / s.cfg.step) * s.cfg.step;
          p!.replay.reset(s);
          if (this.turnMessage) {
            this.turnMessage.bar = s.state.bar;
            this.host.broadcast(this.turnMessage);
          }
          this.correct(p!);
        }
        return;
      }
      case 'join':
        return this.join(id, msg.name, msg.kit);
      case 'setKit':
        return this.setKit(id, msg.kit);
      case 'setOptions':
        return this.setOptions(id, msg.options);
      case 'ready':
        return this.setReady(id, msg.ready);
      case 'start':
        return this.start(id);
      case 'input':
        return this.input(id, msg.batch);
      case 'backToLobby':
        return this.backToLobby(id);
      case 'skipBot':
        return this.skipBot(id);
      case 'requestLeague':
        return this.host.send(id, this.host.getLeague());
      case 'ping':
        return this.host.send(id, { type: 'pong', t: msg.t, serverTime: this.host.now() });
    }
  }

  join(id: string, rawName: string, kit: string): void {
    if (this.players.has(id)) return;
    const name = sanitiseName(rawName);
    if (!name)
      return this.host.send(id, {
        type: 'error',
        message: 'Name must be 2-16 letters or numbers.',
      });
    if (this.humanCount >= MAX_PLAYERS)
      return this.host.send(id, { type: 'error', message: 'The meet is full (6 athletes).' });
    if (this.phase !== 'lobby' && this.phase !== 'podium') {
      return this.host.send(id, {
        type: 'error',
        message: 'A meet is in progress. Wait for it to finish.',
      });
    }
    const taken = [...this.players.values()].some(
      (p) => !p.info.isBot && p.info.name.toLowerCase() === name.toLowerCase(),
    );
    if (taken)
      return this.host.send(id, { type: 'error', message: 'That name is already in the meet.' });
    if (this.players.size >= MAX_PLAYERS) {
      const bot = [...this.players.values()].find((p) => p.info.isBot);
      if (bot) this.players.delete(bot.info.id);
    }
    const isHost = this.humanCount === 0;
    this.players.set(id, {
      replay: new InputReplay(),
      info: {
        id,
        name,
        kit: KIT_RE.test(kit) ? kit : '#1e88e5',
        isBot: false,
        isHost,
        ready: false,
        lives: 3,
        eliminated: false,
        total: 0,
      },
      meet: createMeetAthlete(id),
      sim: null,
      bot: null,
      metrics: {
        sum: 0,
        samples: 0,
        peak: 0,
        presses: 0,
        rejected: 0,
        lastCount: 0,
        lastRejected: 0,
        intervals: [],
      },
      ack: -1,
      disconnectedUntil: null,
      lastInputT: -Infinity,
      recent: [],
    });
    this.host.send(id, { type: 'welcome', id, serverTime: this.host.now() });
    this.broadcastLobby();
  }

  setIdentity(id: string, key: string): void {
    const p = this.players.get(id);
    if (p) p.playerKey = key;
  }
  hasPlayer(id: string): boolean {
    return this.players.has(id);
  }
  disconnect(id: string): void {
    const p = this.players.get(id);
    if (!p) return;
    if (this.phase === 'lobby') {
      this.leave(id);
      return;
    }
    p.info.connected = false;
    p.disconnectedUntil = this.host.now() + RECONNECT_MS;
    p.bot = createBot(this.options.botLevel, mulberry32(this.rng() * 2 ** 32));
    botBeginTurn(p.bot, this.eventTime());
    this.broadcastLobby();
  }
  resume(id: string): boolean {
    const p = this.players.get(id);
    if (!p || p.disconnectedUntil === null || p.disconnectedUntil < this.host.now()) return false;
    p.info.connected = true;
    p.disconnectedUntil = null;
    p.bot = null;
    if (p.sim) p.replay.reset(p.sim);
    this.host.send(id, { type: 'welcome', id, serverTime: this.host.now() });
    if ((this.phase === 'intro' || this.phase === 'event') && this.startMessage)
      this.host.send(id, this.startMessage);
    this.broadcastLobby();
    if (this.phase === 'event' && this.turnMessage) this.host.send(id, this.turnMessage);
    if (p.sim) this.correct(p);
    if ((this.phase === 'results' || this.phase === 'podium') && this.resultMessage)
      this.host.send(id, this.resultMessage);
    return true;
  }
  private correct(p: Player): void {
    if (p.sim && !p.info.isBot && p.info.connected !== false)
      this.host.send(p.info.id, {
        type: 'correction',
        index: this.eventIndex,
        ack: p.ack,
        sim: structuredClone(p.sim),
        serverTime: this.host.now(),
      });
  }
  private expireDisconnected(): void {
    for (const p of this.players.values()) {
      if (p.disconnectedUntil === null || p.disconnectedUntil > this.host.now()) continue;
      p.disconnectedUntil = null;
      p.bot = null;
      p.info.eliminated = true;
      p.meet.eliminated = true;
      p.info.lives = 0;
      p.meet.lives = 0;
      p.info.isHost = false;
      if (p.sim) {
        if (p.sim.kind === 'run') p.sim.state.time = null;
        p.sim.done = true;
      }
    }
    if (![...this.players.values()].some((p) => p.info.isHost && p.info.connected !== false)) {
      const nextHost = [...this.players.values()].find(
        (candidate) => !candidate.info.isBot && candidate.info.connected !== false,
      );
      if (nextHost) nextHost.info.isHost = true;
    }
    if (
      ![...this.players.values()].some(
        (p) => !p.info.isBot && (p.info.connected !== false || p.disconnectedUntil !== null),
      )
    )
      this.resetToLobby();
  }

  leave(id: string): void {
    const p = this.players.get(id);
    if (!p) return;
    this.players.delete(id);
    if (p.info.isHost) {
      const next = [...this.players.values()].find((q) => !q.info.isBot);
      if (next) next.info.isHost = true;
    }
    if (this.humanCount === 0) return this.resetToLobby();
    if (this.phase === 'event') {
      if (this.activeId === id) this.nextTurn();
      else this.maybeFinishEvent();
    }
    this.broadcastLobby();
  }

  private setKit(id: string, kit: string): void {
    const p = this.players.get(id);
    if (!p || !KIT_RE.test(kit)) return;
    p.info.kit = kit;
    this.broadcastLobby();
  }

  private setOptions(id: string, opts: Partial<RoomOptions>): void {
    const p = this.players.get(id);
    if (!p?.info.isHost || this.phase !== 'lobby') return;
    const o = this.options;
    if (typeof opts.programme === 'string') {
      try {
        programmeEvents(opts.programme);
        o.programme = opts.programme;
      } catch {
        /* unknown programme: ignore */
      }
    }
    if (typeof opts.strictHurdles === 'boolean') o.strictHurdles = opts.strictHurdles;
    if (typeof opts.fillWithBots === 'boolean') o.fillWithBots = opts.fillWithBots;
    if (
      opts.botLevel === 'club' ||
      opts.botLevel === 'county' ||
      opts.botLevel === 'national' ||
      opts.botLevel === 'olympic'
    )
      o.botLevel = opts.botLevel;
    this.broadcastLobby();
  }

  private setReady(id: string, ready: boolean): void {
    const p = this.players.get(id);
    if (!p || this.phase !== 'lobby') return;
    p.info.ready = ready;
    this.broadcastLobby();
  }

  private start(id: string): void {
    const p = this.players.get(id);
    if (!p?.info.isHost || this.phase !== 'lobby') return;
    if ([...this.players.values()].some((q) => !q.info.isBot && !q.info.isHost && !q.info.ready))
      return this.host.send(id, { type: 'error', message: 'Wait for every athlete to be ready.' });
    this.season = 1;
    this.beginMeet();
  }

  private backToLobby(id: string): void {
    const p = this.players.get(id);
    if (!p?.info.isHost || this.phase !== 'podium') return;
    this.resetToLobby();
  }

  // --------------------------------------------------------------- meet flow
  private beginMeet(): void {
    this.meetId = this.host.newMeetId?.() ?? `${this.host.now()}-${++this.meetNo}`;
    this.removeBots();
    this.rng = mulberry32((Math.floor(this.host.now()) ^ 0x9e3779b9) >>> 0);
    // Every athlete gets a distinct kit: the first to pick a colour keeps it, clashes and
    // bots take the next free colour from the palette.
    const used = new Set<string>();
    for (const p of this.players.values()) {
      if (used.has(p.info.kit.toLowerCase()))
        p.info.kit = KIT_PALETTE.find((c) => !used.has(c)) ?? p.info.kit;
      used.add(p.info.kit.toLowerCase());
    }
    if (this.options.fillWithBots) {
      let i = 0;
      while (this.players.size < MAX_PLAYERS) {
        const botId = `bot-${i}`;
        const kit = KIT_PALETTE.find((c) => !used.has(c)) ?? '#f5f5f5';
        used.add(kit);
        this.players.set(botId, {
          replay: new InputReplay(),
          info: {
            id: botId,
            name: BOT_NAMES[i] ?? `Bot ${i + 1}`,
            kit,
            isBot: true,
            isHost: false,
            ready: true,
            lives: 3,
            eliminated: false,
            total: 0,
          },
          meet: createMeetAthlete(botId),
          sim: null,
          bot: createBot(this.options.botLevel, mulberry32(this.rng() * 2 ** 32)),
          metrics: {
            sum: 0,
            samples: 0,
            peak: 0,
            presses: 0,
            rejected: 0,
            lastCount: 0,
            lastRejected: 0,
            intervals: [],
          },
          ack: -1,
          disconnectedUntil: null,
          lastInputT: -Infinity,
          recent: [],
        });
        i++;
      }
    }
    for (const p of this.players.values()) {
      p.meet = createMeetAthlete(p.info.id);
      p.info.lives = 3;
      p.info.eliminated = false;
      p.info.total = 0;
      p.info.ready = false;
    }
    this.events = programmeEvents(this.options.programme);
    this.eventIndex = -1;
    this.nextEvent();
  }

  private nextEvent(): void {
    this.eventIndex++;
    const ev = this.events[this.eventIndex];
    if (!ev) return this.podium();
    this.phase = 'intro';
    this.activeId = null;
    this.broadcastLobby();
    // Lanes: humans first, in join order, then bots.
    const ordered = [...this.players.values()].sort(
      (a, b) => Number(a.info.isBot) - Number(b.info.isBot),
    );
    this.lanes = {};
    ordered.forEach((p, i) => (this.lanes[p.info.id] = i));
    const turnBased = isTurnBased(ev);
    const startHeight = Math.round((standardFor(ev, this.season) - 0.1) * 100) / 100;
    const now = this.host.now();
    // The event sim clock starts when the intro ends; the gun comes MARKS_LEAD later.
    const eventAt = now + INTRO_MS;
    const setHold = SET_MIN + this.rng() * (SET_MAX - SET_MIN);
    this.refAt = turnBased ? eventAt : eventAt + MARKS_LEAD * 1000;
    for (const p of this.players.values()) {
      p.sim = p.info.eliminated
        ? null
        : createSim(ev, { startHeight, strictHurdles: this.options.strictHurdles ?? false });
      p.metrics = {
        sum: 0,
        samples: 0,
        peak: 0,
        presses: 0,
        rejected: 0,
        lastCount: 0,
        lastRejected: 0,
        intervals: [],
      };
      p.ack = -1;
      p.lastInputT = -Infinity;
      p.recent = [];
      if (p.sim) p.replay.reset(p.sim);
      if (p.bot) p.bot = createBot(this.options.botLevel, mulberry32(this.rng() * 2 ** 32));
    }
    this.startMessage = {
      type: 'eventStart',
      event: ev.id,
      index: this.eventIndex,
      total: this.events.length,
      refAt: this.refAt,
      setAt: this.refAt - setHold * 1000,
      season: this.season,
      standard: standardFor(ev, this.season),
      startHeight,
      lanes: this.lanes,
      turnBased,
    };
    this.host.broadcast(this.startMessage);
    this.clearTimer();
    this.timer = setTimeout(() => this.startEvent(), INTRO_MS);
  }

  private startEvent(): void {
    this.phase = 'event';
    this.tickNo = 0;
    this.broadcastLobby();
    if (this.tickHandle) clearInterval(this.tickHandle);
    this.tickHandle = setInterval(() => this.tick(), TICK_MS);
    if (this.currentEvent && isTurnBased(this.currentEvent)) this.nextTurn();
  }

  /** Event time (s) at server time `ms`. */
  private eventTime(ms = this.host.now()): number {
    return (ms - this.refAt) / 1000;
  }

  // --------------------------------------------------------------- turns
  /** Pick the next athlete to jump: fewest attempts (long jump) or lowest bar (high jump), then lane order. */
  private pickNext(): Player | null {
    const ev = this.currentEvent;
    if (!ev) return null;
    const cands = [...this.players.values()].filter((p) => p.sim && !p.sim.done);
    if (!cands.length) return null;
    cands.sort(
      (a, b) =>
        turnKey(a.sim!) - turnKey(b.sim!) ||
        (this.lanes[a.info.id] ?? 0) - (this.lanes[b.info.id] ?? 0),
    );
    return cands[0]!;
  }

  private nextTurn(): void {
    if (this.phase !== 'event') return;
    // Once every human is done, play the bots' remaining turns instantly rather than making
    // people watch bots for minutes.
    const humansActive = [...this.players.values()].some(
      (p) => !p.info.isBot && p.sim && !p.sim.done,
    );
    if (!humansActive) {
      this.fastForwardBots();
      return this.finishEvent();
    }
    const p = this.pickNext();
    if (!p) return this.finishEvent();
    const now = this.host.now();
    const t = this.eventTime(now);
    this.activeId = p.info.id;
    p.metrics.lastCount = 0;
    p.metrics.lastRejected = 0;
    simBeginTurn(p.sim!, t);
    p.replay.reset(p.sim!);
    if (p.bot) botBeginTurn(p.bot, t);
    const s = p.sim!;
    this.turnMessage = {
      type: 'turn',
      id: p.info.id,
      refAt: now,
      attempt:
        s.kind === 'lj' || s.kind === 'sp'
          ? s.state.attempt
          : s.kind === 'hj'
            ? s.state.log.length
            : 0,
      bar: s.kind === 'hj' ? s.state.bar : 0,
      clock: ATTEMPT_CLOCK,
    };
    this.host.broadcast(this.turnMessage);
  }

  /**
   * A human asks to skip watching the bot whose turn it is: run that turn to its end
   * instantly and move on. In a meet with several humans only the host may do it.
   */
  private skipBot(id: string): void {
    const who = this.players.get(id);
    if (!who || who.info.isBot || this.phase !== 'event' || !this.activeId) return;
    if (this.humanCount > 1 && !who.info.isHost) return;
    const p = this.players.get(this.activeId);
    if (!p?.sim || !p.bot || p.sim.done) return;
    let t = this.eventTime();
    for (let k = 0; k < 2000 && !simTurnOver(p.sim) && !p.sim.done; k++) {
      t += 0.05;
      stepBot(p.bot, p.sim, t);
      simAdvance(p.sim, t);
    }
    simFinishTurn(p.sim);
    this.nextTurn();
  }

  private fastForwardBots(): void {
    for (const p of this.players.values()) {
      if (!p.sim || p.sim.done || !p.bot) continue;
      let t = this.eventTime();
      for (let guard = 0; guard < 400 && !p.sim.done; guard++) {
        simBeginTurn(p.sim, t);
        botBeginTurn(p.bot, t);
        for (let k = 0; k < 2000 && !simTurnOver(p.sim) && !p.sim.done; k++) {
          t += 0.05;
          stepBot(p.bot, p.sim, t);
          simAdvance(p.sim, t);
        }
        const end = simTurnEndsAt(p.sim);
        if (end !== null) t = end;
        simFinishTurn(p.sim);
      }
    }
  }

  // --------------------------------------------------------------- input and ticks
  private input(id: string, batch: InputBatch): void {
    const p = this.players.get(id);
    if (!p?.sim || this.phase !== 'event' || batch.eventIndex !== this.eventIndex) return;
    if (batch.seq !== undefined) {
      if (batch.seq <= p.ack) return;
      p.ack = batch.seq;
    }
    if (this.activeId !== null && this.activeId !== id) return; // not your turn
    const serverT = this.eventTime();
    const accept = (t: unknown): number | null => {
      if (typeof t !== 'number' || !Number.isFinite(t)) return null;
      // Preserve plausible input times; never accept arbitrary backdating or future simulation.
      let tt = t >= serverT - INPUT_WINDOW && t <= serverT + 0.05 ? Math.min(t, serverT) : serverT;
      if (p.sim!.kind !== 'run') tt = Math.max(tt, p.sim!.attemptStart);
      if (tt < p.lastInputT) tt = p.lastInputT;
      p.lastInputT = tt;
      return tt;
    };
    const items: TimedInput[] = [];
    for (const pr of Array.isArray(batch.presses) ? batch.presses.slice(0, 40) : []) {
      if (pr.key !== 0 && pr.key !== 1) continue;
      items.push({ kind: 'press', key: pr.key, t: pr.t });
    }
    for (const a of Array.isArray(batch.actions) ? batch.actions.slice(0, 10) : [])
      items.push({ kind: 'action', down: !!a.down, t: a.t });
    items.sort((a, b) => a.t - b.t);
    const accepted: TimedInput[] = [];
    for (const it of items) {
      const t = accept(it.t);
      if (t === null) continue;
      if (it.kind === 'press') {
        // Rate limit: more than 16 presses in a second is not a human. Only accepted presses
        // occupy the window, so a burst is trimmed rather than locking the athlete out.
        while (p.recent.length && p.recent[0]! < serverT - 1) p.recent.shift();
        if (p.recent.length >= 16) continue;
        p.recent.push(serverT);
      }
      accepted.push({ ...it, t });
    }
    p.sim = p.replay.apply(p.sim, accepted);
  }

  private tick(): void {
    if (this.phase !== 'event') return;
    this.expireDisconnected();
    if (this.phase !== 'event') return;
    const t = this.eventTime();
    const ev = this.currentEvent!;
    if (isTurnBased(ev)) {
      const p = this.activeId ? this.players.get(this.activeId) : undefined;
      if (p?.sim && !p.sim.done) {
        if (simTurnOver(p.sim)) {
          if (t >= (simTurnEndsAt(p.sim) ?? t)) {
            simFinishTurn(p.sim);
            this.nextTurn();
          }
        } else {
          if (p.bot) stepBot(p.bot, p.sim, t);
          simAdvance(p.sim, t);
        }
      } else if (this.phase === 'event') this.nextTurn();
    } else {
      for (const p of this.players.values()) {
        if (!p.sim) continue;
        if (p.bot) stepBot(p.bot, p.sim, t);
        simAdvance(p.sim, t);
      }
    }
    if (this.phase !== 'event') return;
    for (const p of this.players.values()) {
      this.sampleMetrics(p);
      if (p.sim && !p.bot && (!isTurnBased(ev) || p.info.id === this.activeId))
        p.replay.capture(p.sim);
    }
    if (++this.tickNo % SNAPSHOT_EVERY === 0) {
      for (const p of this.players.values()) this.correct(p);
      this.host.broadcast({
        type: 'snapshot',
        serverTime: this.host.now(),
        athletes: [...this.players.values()]
          .filter((p) => p.sim)
          .map((p) => simSnapshot(p.sim!, p.info.id)),
        activeId: this.activeId,
      });
    }
    this.maybeFinishEvent(t);
  }

  private sampleMetrics(p: Player): void {
    if (!p.sim || (this.activeId !== null && this.activeId !== p.info.id)) return;
    const s = p.sim;
    const c = s.kind === 'run' || s.kind === 'sp' ? s.state.cadence : s.state.runner.cadence;
    const m = p.metrics;
    const added = Math.max(0, c.count - m.lastCount);
    m.presses += added;
    m.rejected += Math.max(0, c.rejected - m.lastRejected);
    m.lastCount = c.count;
    m.lastRejected = c.rejected;
    if (added) m.intervals.push(...c.intervals.slice(-added));
    if (m.intervals.length > 2000) m.intervals.splice(0, m.intervals.length - 2000);
    if (
      c.value > 0.1 &&
      (s.kind === 'run'
        ? !s.state.finished
        : s.state.phase === 'runup' || s.state.phase === 'power')
    ) {
      m.sum += c.value;
      m.samples++;
      m.peak = Math.max(m.peak, c.value);
    }
  }

  private maybeFinishEvent(t = this.eventTime()): void {
    if (this.phase !== 'event') return;
    const ev = this.currentEvent!;
    const active = [...this.players.values()].filter((p) => p.sim);
    const allDone = active.every((p) => p.sim!.done);
    const capped = isTurnBased(ev) ? t > FIELD_CAP : t > runCapFor(ev) + 1;
    if (allDone || capped) this.finishEvent();
  }

  private finishEvent(): void {
    if (this.phase !== 'event') return;
    if (this.tickHandle) clearInterval(this.tickHandle);
    this.tickHandle = null;
    const ev = this.currentEvent!;
    this.phase = 'results';
    this.activeId = null;
    // Final authoritative advance so late finishers get their marks.
    const t = this.eventTime();
    const marks: { id: string; mark: number | null }[] = [];
    for (const p of this.players.values()) {
      if (!p.sim) continue;
      if (!isTurnBased(ev)) simAdvance(p.sim, t);
      marks.push({ id: p.info.id, mark: simMark(p.sim) });
    }
    const places = eventPlaces(ev, marks);
    const rows: EventResultRow[] = [];
    for (const p of this.players.values()) {
      if (!p.sim) {
        rows.push({
          id: p.info.id,
          name: p.info.name,
          kit: p.info.kit,
          isBot: p.info.isBot,
          mark: null,
          points: 0,
          place: 0,
          total: p.meet.total,
          standing: 0,
          passed: false,
          lives: p.meet.lives,
          eliminated: true,
          participated: false,
        });
        continue;
      }
      const mark = simMark(p.sim);
      const { points, passed } = applyMark(p.meet, ev, mark, this.season);
      if (!p.info.isBot && this.host.recordEvent) {
        try {
          this.host.recordEvent(
            p.playerKey ?? p.info.name.toLowerCase(),
            p.info.name,
            {
              event: ev.id,
              mark,
              points,
              mode: 'online',
              playedAt: new Date(this.host.now()).toISOString(),
              cadence: {
                avgCadence: p.metrics.samples ? p.metrics.sum / p.metrics.samples : 0,
                peakCadence: p.metrics.peak,
                presses: p.metrics.presses,
                rejected: p.metrics.rejected,
                suspicious: regularity(p.metrics.intervals).suspicious,
              },
            },
            `${this.meetId}:${p.info.id}:${ev.id}`,
          );
        } catch (e) {
          this.host.log?.(`recordEvent failed: ${String(e)}`);
        }
      }
      p.info.total = p.meet.total;
      p.info.lives = p.meet.lives;
      p.info.eliminated = p.meet.eliminated;
      rows.push({
        id: p.info.id,
        name: p.info.name,
        kit: p.info.kit,
        isBot: p.info.isBot,
        mark,
        points,
        place: places.get(p.info.id) ?? rows.length + 1,
        total: p.meet.total,
        standing: 0,
        passed,
        lives: p.meet.lives,
        eliminated: p.meet.eliminated,
      });
    }
    standings(rows).forEach(
      (r) =>
        (r.standing = [...this.players.values()].filter((p) => p.meet.total > r.total).length + 1),
    );
    rows.sort((a, b) => (a.place || Infinity) - (b.place || Infinity));
    const nextAt = this.host.now() + RESULTS_MS;
    this.resultMessage = {
      type: 'eventResults',
      meetId: this.meetId,
      event: ev.id,
      index: this.eventIndex,
      total: this.events.length,
      results: rows,
      nextAt,
    };
    this.host.broadcast(this.resultMessage);
    this.broadcastLobby();
    this.clearTimer();
    // No humans left standing: the meet ends here.
    const humansLeft = [...this.players.values()].some((p) => !p.info.isBot && !p.info.eliminated);
    this.timer = setTimeout(() => (humansLeft ? this.nextEvent() : this.podium()), RESULTS_MS);
  }

  private podium(): void {
    this.phase = 'podium';
    this.clearTimer();
    const all = standings([...this.players.values()].map((p) => ({ p, total: p.meet.total })));
    const base = all.map(({ p }) => {
      const bestEvent = Math.max(0, ...Object.values(p.meet.points));
      return {
        id: p.info.id,
        name: p.info.name,
        kit: p.info.kit,
        isBot: p.info.isBot,
        total: p.meet.total,
        place: all.filter((a) => a.total > p.meet.total).length + 1,
        ...(p.playerKey ? { playerKey: p.playerKey } : {}),
        marks: { ...p.meet.marks },
        points: { ...p.meet.points },
        bestEvent,
        eliminated: p.meet.eliminated,
      };
    });
    const ranked =
      this.options.programme === 'full' &&
      base.filter((p) => !p.isBot).length >= 2 &&
      this.eventIndex === this.events.length;
    const results = leaguePoints(base).map((r) =>
      ranked ? r : { ...r, leaguePoints: 0, bestEventBonus: false },
    );
    try {
      this.host.recordMeet(results, this.options.programme, this.meetId);
    } catch (e) {
      this.host.log?.(`recordMeet failed: ${String(e)}`);
    }
    this.resultMessage = { type: 'podium', results, programme: this.options.programme };
    this.host.broadcast(this.resultMessage);
    this.host.broadcast(this.host.getLeague());
    this.broadcastLobby();
    this.timer = setTimeout(() => this.resetToLobby(), PODIUM_MS);
  }

  private resetToLobby(): void {
    this.clearTimer();
    if (this.tickHandle) clearInterval(this.tickHandle);
    this.tickHandle = null;
    this.removeBots();
    for (const [id, p] of this.players) if (p.info.connected === false) this.players.delete(id);
    const host =
      [...this.players.values()].find((p) => !p.info.isBot && p.info.isHost) ??
      [...this.players.values()].find((p) => !p.info.isBot);
    for (const p of this.players.values()) {
      p.info.isHost = p === host;
      p.sim = null;
      p.info.ready = false;
    }
    this.phase = 'lobby';
    this.activeId = null;
    this.broadcastLobby();
  }

  private clearTimer(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }

  private removeBots(): void {
    for (const [id, p] of this.players) if (p.info.isBot) this.players.delete(id);
  }

  private broadcastLobby(): void {
    this.host.broadcast({
      type: 'lobby',
      players: this.playerList,
      options: { ...this.options },
      phase: this.phase,
    });
  }

  dispose(): void {
    this.clearTimer();
    if (this.tickHandle) clearInterval(this.tickHandle);
  }
}
